// O8 surfaces keep their state across a switch. Unknown routes remain scoped.
const UNSCOPED_PATHS = [
  '/api/ava', '/api/support', '/api/auth', '/api/workforce',
  '/api/coverage', '/api/customer', '/api/consult', '/api/consultant',
  '/api/subscriptions', '/api/sales', '/api/bundles', '/api/reports',
  '/api/locations', '/api/tiles', '/api/admin/coverage', '/api/admin/operators',
  '/api/admin/forms', '/api/admin/front', '/api/admin/customers', '/api/admin/customer-registrations',
  '/api/admin/consultants', '/api/admin/contractors', '/api/admin/models',
  '/api/admin/subscription', '/api/admin/inspection-settings',
  '/api/admin/model-settings', '/api/admin/provider-settings', '/api/admin/reasoning-settings',
  '/api/admin/design-explanation-settings', '/api/admin/inspection-model-defaults',
  '/api/admin/models-registry', '/api/admin/openrouter-balance',
  '/api/admin/subscription-health', '/api/admin/subscription-config', '/api/admin/profiles',
  '/sb/auth', '/sb/storage',
];

const MUTATIONS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
export const WORKFORCE_FETCH_TIMEOUT_MS = Number(
  process.env.REACT_APP_SUPABASE_FETCH_TIMEOUT_MS || 20000,
);

let csrfToken = null;
let designAccessToken = null;
let installed = false;
let originalFetch = null;
let activeCarrierId = null;
let carrierAbort = null;

export function setActiveCarrierId(id) {
  const next = id || null;
  installWorkforceFetchBridge();
  if (next === activeCarrierId) return;
  carrierAbort?.abort();
  activeCarrierId = next;
  carrierAbort = next ? new AbortController() : null;
}

export function getActiveCarrierId() { return activeCarrierId; }

// Dispose fallback listeners when the request settles, not just on abort.
function combineSignals(signals) {
  const present = signals.filter(Boolean);
  if (present.length < 2) return { signal: present[0], dispose: () => {} };
  if (typeof AbortSignal.any === 'function') {
    return { signal: AbortSignal.any(present), dispose: () => {} };
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  present.forEach((signal) => {
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
  });
  return {
    signal: controller.signal,
    dispose: () => present.forEach((signal) => signal.removeEventListener('abort', abort)),
  };
}

function ensureOriginalFetch() {
  if (!originalFetch && typeof window !== 'undefined' && typeof window.fetch === 'function') {
    originalFetch = window.fetch.bind(window);
  }
  return originalFetch;
}

function requestUrl(input) {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(typeof input === 'string' ? input : input.url, window.location.origin);
  } catch (_error) {
    return null;
  }
}

function withHeaders(headers) {
  return new Headers(headers || {});
}

function installWorkforceFetchBridge() {
  if (typeof window === 'undefined' || installed || !ensureOriginalFetch()) return;
  installed = true;
  window.fetch = (input, init = {}) => {
    const url = requestUrl(input);
    if (!url || url.origin !== window.location.origin
        || (!url.pathname.startsWith('/api/') && !url.pathname.startsWith('/sb/'))) {
      return originalFetch(input, init);
    }
    const method = String(init.method || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    const headers = withHeaders(init.headers || (typeof input !== 'string' ? input.headers : undefined));
    if (csrfToken && MUTATIONS.has(method)) headers.set('X-CSRF-Token', csrfToken);
    if (designAccessToken) headers.set('Authorization', 'Bearer ' + designAccessToken);
    // Never forward a stale manually supplied carrier header.
    if (activeCarrierId) headers.set('X-Carrier-Id', activeCarrierId);
    else headers.delete('X-Carrier-Id');
    const callerSignal = init.signal || (typeof input !== 'string' ? input.signal : undefined);
    const unscoped = UNSCOPED_PATHS.some((path) => url.pathname === path || url.pathname.startsWith(`${path}/`));
    const combined = combineSignals([callerSignal, unscoped ? null : carrierAbort?.signal]);
    return Promise.resolve(originalFetch(input, {
      ...init,
      method,
      headers,
      signal: combined.signal,
      credentials: 'same-origin',
    })).finally(combined.dispose);
  };
}

function activateAdminSession(payload) {
  csrfToken = payload && payload.csrf_token ? payload.csrf_token : null;
  designAccessToken = null;
  installWorkforceFetchBridge();
  return payload;
}

export async function resumeWorkforceBffSession() {
  if (!ensureOriginalFetch()) return null;
  const response = await originalFetch('/api/workforce/session', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  return activateAdminSession(await response.json());
}

export async function exchangeSupabaseSessionForBff(session) {
  if (!ensureOriginalFetch() || !session?.access_token || !session?.refresh_token) return null;
  const response = await originalFetch('/api/workforce/session', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      Authorization: 'Bearer ' + session.access_token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: session.refresh_token }),
  });
  if (!response.ok) return null;
  return activateAdminSession(await response.json());
}

export async function signOutWorkforceBff() {
  if (!ensureOriginalFetch() || !csrfToken) return false;
  const response = await originalFetch('/api/workforce/session', {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken },
  });
  if (!response.ok && response.status !== 204) return false;
  csrfToken = null;
  return true;
}

export async function startOldHostSessionTransfer(session, adminOrigin) {
  if (!ensureOriginalFetch() || !session?.access_token || !session?.refresh_token) return false;
  const response = await originalFetch('/api/workforce/transfer', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: 'Bearer ' + session.access_token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: session.refresh_token }),
  });
  if (!response.ok) return false;
  const payload = await response.json();
  const destination = new URL('/api/workforce/transfer/redeem', adminOrigin || payload.destination_origin);
  const form = document.createElement('form');
  form.method = 'post';
  form.action = destination.toString();
  const field = document.createElement('input');
  field.type = 'hidden';
  field.name = 'transfer_ticket';
  field.value = payload.transfer_ticket;
  form.appendChild(field);
  document.body.appendChild(form);
  form.submit();
  return true;
}

export async function discoverDesignBootstrap() {
  if (!ensureOriginalFetch()) return null;
  const response = await originalFetch('/api/workforce/bootstrap-config', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  return response.json();
}

export function beginTopLevelDesignBootstrap(adminOrigin = 'https://admin.fttx.ir') {
  const destination = new URL('/api/workforce/design/authorize', adminOrigin);
  window.location.assign(destination.toString());
}

export function consumeDesignBootstrapFromWindowName() {
  if (typeof window === 'undefined' || !window.name) return null;
  let payload;
  try {
    payload = JSON.parse(window.name);
  } catch (_error) {
    return null;
  }
  if (!payload || payload.type !== 'fttx-design-bootstrap'
      || typeof payload.access_token !== 'string'
      || !payload.access_token.startsWith('wfd_')) return null;
  window.name = '';
  designAccessToken = payload.access_token;
  csrfToken = null;
  installWorkforceFetchBridge();
  return payload;
}

export function workforceFetch(input, init) {
  if (typeof window === 'undefined') return fetch(input, init);
  installWorkforceFetchBridge();
  const controller = new AbortController();
  const callerSignal = init?.signal;
  let removeCallerAbort;
  let timedOut = false;
  let signal = controller.signal;

  if (callerSignal) {
    if (typeof AbortSignal.any === 'function') {
      signal = AbortSignal.any([callerSignal, controller.signal]);
    } else {
      const abort = () => controller.abort();
      if (callerSignal.aborted) abort();
      else {
        callerSignal.addEventListener('abort', abort, { once: true });
        removeCallerAbort = () => callerSignal.removeEventListener('abort', abort);
      }
    }
  }

  let timeoutId;
  const timeoutError = new Error('Request timed out');
  timeoutError.code = 'TIMEOUT';
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(timeoutError);
    }, WORKFORCE_FETCH_TIMEOUT_MS);
  });
  const request = Promise.resolve(window.fetch(input, { ...init, signal }))
    .catch((error) => {
      if (!timedOut) throw error;
      throw timeoutError;
    });

  return Promise.race([request, timeout]).finally(() => {
    clearTimeout(timeoutId);
    removeCallerAbort?.();
  });
}

export function currentWorkforceCsrfToken() {
  return csrfToken;
}

export function currentDesignAccessToken() {
  return designAccessToken;
}

export function clearDesignAccessToken() {
  designAccessToken = null;
  if (typeof window !== 'undefined') window.name = '';
}
